import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import AdminPageHeader from './AdminPageHeader';
import LivePreview from './LivePreview';
import EditModal from './EditModal';
import { FaBullhorn, FaArrowUp, FaArrowDown, FaEdit, FaTrash, FaPlus, FaEye } from 'react-icons/fa';
import { API_ENDPOINTS, getImageUrl } from '../../utils/GlobalAPI';

// Helper function to handle base64 data URLs or regular URLs
const getImageSource = (image) => {
  if (!image) return null;
  // If it's a base64 data URL (starts with data:), use it directly
  if (image.startsWith('data:')) {
    return image;
  }
  // If it's a full URL, use it directly
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }
  // Otherwise, construct the URL using getImageUrl
  return getImageUrl(image);
};

const BannerEdit = () => {
  const [banner, setBanner] = useState({ 
    image: '', 
    title: '', 
    subtext: '',
    subtitle: '',
    order: 0
  });
  const [banners, setBanners] = useState([]);
  const [editIdx, setEditIdx] = useState(null);
  const [editBanner, setEditBanner] = useState({ 
    image: '', 
    title: '', 
    subtext: '',
    subtitle: '',
    order: 0
  });
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.BANNERS);
      const sortedBanners = res.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setBanners(sortedBanners);
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  };

  const toBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleChange = async e => {
    const { name, files, value } = e.target;
    if (files) {
      const base = await toBase64(files[0]);
      setBanner(prev => ({ ...prev, image: base }));
    } else {
      setBanner(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleOrderChange = async (idx, direction) => {
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === banners.length - 1)) {
      return;
    }
    const newBanners = [...banners];
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newBanners[idx].order, newBanners[targetIdx].order] = [newBanners[targetIdx].order, newBanners[idx].order];
    
    try {
      await Promise.all([
        axios.put(`${API_ENDPOINTS.BANNERS}/${newBanners[idx]._id}`, { order: newBanners[idx].order }),
        axios.put(`${API_ENDPOINTS.BANNERS}/${newBanners[targetIdx]._id}`, { order: newBanners[targetIdx].order })
      ]);
      setBanners(newBanners.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const bannerData = {
        ...banner,
        order: banners.length > 0 ? Math.max(...banners.map(b => b.order || 0)) + 1 : 0
      };
      const { data } = await axios.post(API_ENDPOINTS.BANNERS, bannerData);
      await fetchBanners();
      setBanner({ image: '', title: '', subtext: '', subtitle: '', order: 0 });
    } catch (e) {
      console.error(e);
      alert('Error saving banner: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = idx => {
    setEditIdx(idx);
    setEditBanner({ 
      ...banners[idx],
      subtitle: banners[idx].subtitle || banners[idx].subtext || ''
    });
  };

  const handleEditChange = async e => {
    const { name, files, value } = e.target;
    if (files) {
      const base = await toBase64(files[0]);
      setEditBanner(prev => ({ ...prev, image: base }));
    } else {
      setEditBanner(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEditSave = async () => {
    setLoading(true);
    try {
      const id = editBanner._id;
      const { data } = await axios.put(`${API_ENDPOINTS.BANNERS}/${id}`, editBanner);
      await fetchBanners();
      setEditIdx(null);
      setEditBanner({ image: '', title: '', subtext: '', subtitle: '', order: 0 });
    } catch (e) {
      console.error(e);
      alert('Error updating banner: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEdit = () => {
    setEditIdx(null);
    setEditBanner({ image: '', title: '', subtext: '', subtitle: '', order: 0 });
  };

  const handleDelete = async idx => {
    if (!window.confirm('Delete this banner slide?')) return;
    try {
      await axios.delete(`${API_ENDPOINTS.BANNERS}/${banners[idx]._id}`);
      await fetchBanners();
    } catch (e) {
      console.error(e);
      alert('Error deleting banner: ' + (e.response?.data?.error || e.message));
    }
  };

  useEffect(() => {
    if (previewMode && banners.length > 0) {
      const interval = setInterval(() => {
        setCurrentPreviewIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [previewMode, banners.length]);

  return (
    <div className="bg-gray-50 min-h-screen">
      <AdminPageHeader
        title="Hero Carousel Control Panel"
        subtitle="Manage homepage carousel slides with images and content"
        icon={<FaBullhorn className="w-6 h-6" />}
      />
      
      <div className="pt-32 pb-8 px-6 max-w-7xl mx-auto">
        {/* Preview Toggle Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-1">Carousel Slides</h2>
              <p className="text-sm text-gray-500">Manage your homepage hero carousel content</p>
            </div>
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="px-6 py-2.5 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 text-white rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              {previewMode ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>
        </div>

        {/* Carousel Preview */}
        {previewMode && banners.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-8"
          >
            <div className="relative h-96">
              <motion.div
                key={currentPreviewIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5 }}
                className="absolute inset-0"
              >
                <img
                  src={getImageSource(banners[currentPreviewIndex]?.image)}
                  alt={banners[currentPreviewIndex]?.title || 'Banner preview'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/1920x1080?text=Image+Error';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/60" />
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/20 via-orange-600/20 via-pink-600/20 via-purple-600/20 to-blue-600/20" />
              </motion.div>
              
              <div className="absolute inset-0 flex items-center justify-center z-10 text-center px-6">
                <div>
                  <h3 className="text-3xl md:text-4xl font-light text-white mb-4 drop-shadow-lg">
                    {banners[currentPreviewIndex]?.title}
                  </h3>
                  <p className="text-lg md:text-xl font-light text-white/90 drop-shadow-md mb-2">
                    {banners[currentPreviewIndex]?.subtitle || banners[currentPreviewIndex]?.subtext}
                  </p>
                </div>
              </div>

              {/* Carousel Indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                {banners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPreviewIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentPreviewIndex ? 'bg-white w-8' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Add New Slide Form */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8 mb-8">
          <div className="flex items-center mb-6 pb-4 border-b border-gray-200">
            <div className="p-2 rounded-lg bg-gradient-to-br from-orange-100 to-pink-100 mr-3">
              <FaPlus className="text-orange-500" size={20} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Add New Carousel Slide</h3>
              <p className="text-sm text-gray-500 mt-0.5">Create a new slide for your homepage carousel</p>
            </div>
          </div>
          {loading && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-600 font-medium text-sm">
              Saving...
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form Section */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Carousel Image *</label>
                  <input
                    type="file" 
                    name="image" 
                    accept="image/*"
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0 file:bg-gradient-to-r file:from-orange-100 file:via-pink-100 file:to-purple-100
                      file:text-orange-600 file:text-sm file:font-semibold hover:file:opacity-80"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Main Title *</label>
                  <input
                    name="title" 
                    value={banner.title} 
                    onChange={handleChange}
                    placeholder="e.g., AI & Digital Transformation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none placeholder:text-gray-500" 
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle/Description *</label>
                  <textarea
                    name="subtext" 
                    value={banner.subtext} 
                    onChange={handleChange}
                    placeholder="e.g., Transform your business with expert digital marketing and cutting-edge technology solutions."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none resize-none placeholder:text-gray-500" 
                    style={{ color: '#111827', backgroundColor: '#ffffff' }}
                    required
                  />
                </div>
              </div>

              <button 
                type="submit"
                className="w-full bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 hover:opacity-90
                  text-white py-3 rounded-lg font-semibold transition shadow-md"
              >
                Add Slide to Carousel
              </button>
            </form>

            {/* Live Preview Section */}
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-2 mb-3">
                <FaEye className="text-orange-500" />
                <h4 className="text-sm font-semibold text-gray-700">Live Canvas Preview</h4>
              </div>
              <LivePreview 
                image={banner.image || (banners.length > 0 ? banners[0]?.image : null)}
                title={banner.title || (banners.length > 0 ? banners[0]?.title : 'AI & Digital Transformation')}
                subtext={banner.subtext || (banners.length > 0 ? banners[0]?.subtext : 'Transform your business with expert digital marketing and cutting-edge technology solutions.')}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Preview updates in real-time as you type
              </p>
            </div>
          </div>
        </div>

        {/* Carousel Slides List */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-8">
          <div className="mb-6 pb-4 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-800 mb-1">Manage Carousel Slides</h3>
            <p className="text-sm text-gray-500">
              {banners.length === 0 
                ? 'No slides yet. Add your first slide above.' 
                : `${banners.length} ${banners.length === 1 ? 'slide' : 'slides'} configured`}
            </p>
          </div>
          
          {banners.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
                <FaBullhorn className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium mb-1">No carousel slides yet</p>
              <p className="text-sm text-gray-500">Add your first slide using the form above</p>
            </div>
          ) : (
            <div className="space-y-4">
              {banners.map((b, idx) => (
                <motion.div
                  key={b._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
                >
                  <div className="flex items-center gap-5">
                    <div className="flex-shrink-0">
                      <img 
                        src={getImageSource(b.image)} 
                        alt={b.title || 'Banner slide'} 
                        className="w-36 h-24 object-cover rounded-lg border border-gray-200 shadow-sm"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/144x96?text=No+Image';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 mb-1 truncate">{b.title}</h4>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{b.subtext}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="px-2 py-0.5 bg-gray-100 rounded">Order: {b.order || idx + 1}</span>
                        <span className="px-2 py-0.5 bg-gradient-to-r from-orange-50 to-pink-50 text-orange-600 rounded">Slide {idx + 1}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOrderChange(idx, 'up')}
                        disabled={idx === 0}
                        className="p-2.5 text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition"
                        title="Move up"
                      >
                        <FaArrowUp size={14} />
                      </button>
                      <button
                        onClick={() => handleOrderChange(idx, 'down')}
                        disabled={idx === banners.length - 1}
                        className="p-2.5 text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition"
                        title="Move down"
                      >
                        <FaArrowDown size={14} />
                      </button>
                      <button
                        onClick={() => handleEditClick(idx)}
                        className="p-2.5 text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition"
                        title="Edit"
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(idx)}
                        className="p-2.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Modal */}
        <EditModal
          isOpen={editIdx !== null}
          onClose={handleCloseEdit}
          title={`Edit Slide ${editIdx !== null ? editIdx + 1 : ''}`}
          onSave={handleEditSave}
          saveLabel="Save Changes"
          isLoading={loading}
          maxWidth="max-w-5xl"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form Section */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Carousel Image</label>
                <input 
                  type="file" 
                  name="image" 
                  accept="image/*"
                  onChange={handleEditChange} 
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
                {editBanner.image && (
                  <img src={editBanner.image} alt="Preview" className="mt-3 w-full h-32 object-cover rounded-lg border border-gray-200" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input 
                  name="title" 
                  value={editBanner.title} 
                  onChange={handleEditChange} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none" 
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle *</label>
                <textarea 
                  name="subtext" 
                  value={editBanner.subtext} 
                  onChange={handleEditChange} 
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg !text-gray-900 !bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none resize-none" 
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  required
                />
              </div>
            </div>

            {/* Live Preview */}
            <div className="lg:sticky lg:top-0">
              <div className="flex items-center gap-2 mb-3">
                <FaEye className="text-orange-500" />
                <h4 className="text-sm font-semibold text-gray-700">Live Preview</h4>
              </div>
              <LivePreview 
                image={editBanner.image || (editIdx !== null ? banners[editIdx]?.image : null)}
                title={editBanner.title || (editIdx !== null ? banners[editIdx]?.title : '')}
                subtext={editBanner.subtext || (editIdx !== null ? banners[editIdx]?.subtext : '')}
              />
            </div>
          </div>
        </EditModal>
      </div>
    </div>
  );
};

export default BannerEdit;
